// CommandHideControl — per-user hide/unhide for global Workbench &
// Refinery commands. May 25, 2026.
//
// Sits to the right of the Operation dropdown. Two buttons in one:
//   • "Hide" — mutes the currently-selected global command from THIS
//     user's dropdown. Only enabled when the selected command is global
//     (custom commands can be deleted normally).
//   • "Unhide…" — opens a small popover listing the user's hidden
//     commands. Click any to unhide. Only shown when count > 0.

import { useEffect, useState } from "react";
import { EyeSlash, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function CommandHideControl({ scope, selectedCmd, onReload }) {
  const [hidden, setHidden] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadHidden = async () => {
    try {
      const r = await api.get(`/me/commands/hidden?scope=${scope}`);
      setHidden(r.data?.hidden || []);
    } catch {
      // ignore — feature is non-critical
    }
  };

  useEffect(() => { loadHidden(); /* eslint-disable-next-line */ }, [scope]);

  const hide = async () => {
    if (!selectedCmd || !selectedCmd.is_global) {
      toast.error("Pick a global command first (your own custom commands can be deleted instead).");
      return;
    }
    setBusy(true);
    try {
      await api.post("/me/commands/hide", { cmd_id: selectedCmd.id });
      toast.success(`Hidden "${selectedCmd.name}" from your dropdown.`);
      await loadHidden();
      if (onReload) await onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not hide.");
    } finally {
      setBusy(false);
    }
  };

  const unhide = async (cmdId, name) => {
    setBusy(true);
    try {
      await api.post("/me/commands/unhide", { cmd_id: cmdId });
      toast.success(`Restored "${name}".`);
      await loadHidden();
      if (onReload) await onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not unhide.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex items-center gap-1 shrink-0" data-testid={`cmd-hide-control-${scope}`}>
      <button
        type="button"
        onClick={hide}
        disabled={busy || !selectedCmd?.is_global}
        data-testid={`cmd-hide-btn-${scope}`}
        title={selectedCmd?.is_global ? `Hide "${selectedCmd.name}" from your dropdown` : "Pick a global command to hide it"}
        className="p-2 rounded-md border hairline text-[#6B6A68] hover:text-[#C16E5A] hover:border-[#C16E5A] disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <EyeSlash size={14} weight="duotone" />
      </button>
      {hidden.length > 0 && (
        <div className="relative">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid={`cmd-unhide-toggle-${scope}`}
            title="View / restore your hidden commands"
            className="px-2 py-2 rounded-md border hairline text-[#6B6A68] hover:text-[#2F6B3A] hover:border-[#2F6B3A] flex items-center gap-1 text-xs"
          >
            <Eye size={12} weight="duotone" />
            <span className="tabular-nums">{hidden.length}</span>
          </button>
          {open && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
              <div
                className="absolute right-0 top-full mt-1 w-64 bg-white border hairline rounded-md shadow-lg z-40"
                data-testid={`cmd-unhide-menu-${scope}`}
              >
                <div className="px-3 py-2 border-b hairline text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">
                  Hidden — click to restore
                </div>
                <ul className="max-h-72 overflow-y-auto">
                  {hidden.map((h) => (
                    <li key={h.id}>
                      <button
                        type="button"
                        onClick={() => { unhide(h.id, h.name); setOpen(false); }}
                        data-testid={`cmd-unhide-item-${scope}-${h.id}`}
                        className="w-full text-left px-3 py-2 text-xs text-[#1C1B1A] hover:bg-[#F0F1EE] flex items-center gap-2"
                      >
                        <Eye size={11} weight="duotone" className="text-[#2F6B3A] shrink-0" />
                        <span className="truncate">{h.name}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
